import { BalaClient, type ConnectionState } from '@bala/core';
import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Client partagé de l'application Staff. Une seule instance : elle porte le
 * jeton de session, le cache hors connexion et la connexion temps réel.
 */
/**
 * L'adresse de l'API.
 *
 * Par défaut un chemin relatif : les pages et l'API sont servies par la même
 * origine, et rien n'a besoin d'être configuré. Quand le front est hébergé
 * ailleurs que l'API — Vercel d'un côté, Railway de l'autre — `VITE_API_URL`
 * porte l'adresse absolue, fixée au moment du build.
 *
 * Les deux doivent rester sur le **même domaine racine** (`staff.exemple.be` et
 * `api.exemple.be`) : le cookie de session est en `SameSite=Lax`, et un
 * navigateur ne l'enverrait pas vers un autre site. Cette variable ne change
 * rien à cette règle — elle ne fait que dire où appeler.
 */
const BASE_URL = import.meta.env.VITE_API_URL?.replace(/\/+$/, '') || '/api';

export const api = new BalaClient({
  baseUrl: BASE_URL,
  storeName: 'bala-staff',
  app: 'staff',
  legacyTokenKey: 'bala.staff.token',
});

// ---------------------------------------------------------------------------
// État de la connexion
// ---------------------------------------------------------------------------

export function useConnection(): { state: ConnectionState; pending: number } {
  const [state, setState] = useState<ConnectionState>(api.getState());
  const [pending, setPending] = useState(0);

  useEffect(() => {
    const offState = api.onStateChange(setState);
    const offOutbox = api.onOutboxChange((entries) => setPending(entries.length));
    void api.pendingCount().then(setPending);
    return () => {
      offState();
      offOutbox();
    };
  }, []);

  return { state, pending };
}

// ---------------------------------------------------------------------------
// Chargement de données
// ---------------------------------------------------------------------------

export interface QueryResult<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
  /** Rechargement manuel, après une action de l'utilisateur. */
  reload: () => void;
}

/**
 * Charge une route de l'API et se rafraîchit quand le serveur signale un
 * changement sur l'une des entités surveillées.
 *
 * Le rafraîchissement est groupé sur 250 ms : un encodage de présences en lot
 * émet une rafale d'événements, et on ne veut qu'un seul rechargement.
 */
export function useQuery<T>(
  path: string | null,
  entities: string[] = [],
  options: { cache?: boolean } = {},
): QueryResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(path !== null);
  const [nonce, setNonce] = useState(0);

  const entityKey = entities.join(',');
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  const reload = useCallback(() => setNonce((value) => value + 1), []);

  useEffect(() => {
    if (!path) {
      setData(null);
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    let active = true;
    setLoading(true);

    api
      .get<T>(path, { signal: controller.signal, cache: options.cache !== false })
      .then((result) => {
        if (!active) return;
        setData(result);
        setError(null);
      })
      .catch((caught: unknown) => {
        if (!active) return;
        if (caught instanceof DOMException && caught.name === 'AbortError') return;
        setError(caught instanceof Error ? caught.message : 'Erreur inconnue');
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, nonce]);

  useEffect(() => {
    if (!path || entities.length === 0) return;
    const watched = new Set(entityKey.split(','));

    return api.onChange((change) => {
      if (!watched.has(change.entity)) return;
      if (debounce.current) clearTimeout(debounce.current);
      debounce.current = setTimeout(reload, 250);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, entityKey, reload]);

  return { data, error, loading, reload };
}

/**
 * Préfixes IndexedDB des GET dont le solde dépend du capital de départ.
 * Invalider ces clés force Accueil, Finances et Statistiques à reprendre
 * une réponse fraîche — y compris hors connexion après la prochaine sync.
 */
export const FINANCE_BALANCE_CACHE_PREFIXES = [
  'GET:/settings',
  'GET:/finance/transactions',
  'GET:/finance/report',
  'GET:/stats',
];

/**
 * Après une modification du capital de départ : jette les caches du solde et
 * prévient les écrans ouverts dans cet onglet (sans dépendre du WebSocket).
 */
export async function invalidateFinanceBalanceQueries(): Promise<void> {
  await api.invalidateCache(FINANCE_BALANCE_CACHE_PREFIXES);
  api.notifyChange({
    entity: 'settings',
    entityId: 'financeOpeningBalanceCents',
    op: 'updated',
    actorId: null,
  });
  api.notifyChange({
    entity: 'transactions',
    entityId: 'opening-balance',
    op: 'updated',
    actorId: null,
  });
}

/**
 * Exécute une mutation en suivant son état. Le message d'erreur est conservé
 * pour affichage : le mode hors connexion renvoie une erreur « mise en file »
 * qui n'est pas un échec.
 */
export function useMutation<TArgs extends unknown[], TResult>(
  fn: (...args: TArgs) => Promise<TResult>,
): {
  run: (...args: TArgs) => Promise<TResult | null>;
  pending: boolean;
  error: string | null;
  queued: boolean;
} {
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [queued, setQueued] = useState(false);

  const run = useCallback(
    async (...args: TArgs) => {
      setPending(true);
      setError(null);
      setQueued(false);
      try {
        return await fn(...args);
      } catch (caught) {
        const message = caught instanceof Error ? caught.message : 'Erreur inconnue';
        // Une mutation enregistrée hors connexion sera envoyée plus tard : ce
        // n'est pas une erreur à afficher en rouge.
        const isQueued = typeof caught === 'object' && caught !== null && 'code' in caught && (caught as { code?: string }).code === 'queued';
        setQueued(isQueued);
        setError(isQueued ? null : message);
        return null;
      } finally {
        setPending(false);
      }
    },
    [fn],
  );

  return { run, pending, error, queued };
}

/** Valeur persistée dans `localStorage`, pour les préférences d'affichage. */
export function useLocalState<T>(key: string, initial: T): [T, (next: T) => void] {
  const [value, setValue] = useState<T>(() => {
    if (typeof localStorage === 'undefined') return initial;
    const stored = localStorage.getItem(key);
    if (stored == null) return initial;
    try {
      return JSON.parse(stored) as T;
    } catch {
      return initial;
    }
  });

  const update = useCallback(
    (next: T) => {
      setValue(next);
      try {
        localStorage.setItem(key, JSON.stringify(next));
      } catch {
        /* quota dépassé ou stockage désactivé : la préférence reste en mémoire */
      }
    },
    [key],
  );

  return [value, update];
}

/** Retarde la propagation d'une valeur — utilisé par les champs de recherche. */
export function useDebounced<T>(value: T, delay = 250): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}
